import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { AccountLettrageStats, LettrageEntryLine, LettrageFilters } from '../types'

const QUERY_KEY = 'lettrage'

// Interface pour les montants de lettrage
export interface LettrageLineAmount {
  lineId: string
  amount: number
}

// Interface locale pour typer les résultats des requêtes
interface TransactionLineWithLettrage {
  id: string
  residence_chart_account_id: string
  debit: number | null
  credit: number | null
  owner_id: string | null
  unit_id: string | null
  label: string | null
  transaction_id: string
  // Colonnes calculées de la vue transaction_lines_with_lettrage
  lettered_amount?: number
  remaining_amount?: number
  lettrage_status?: 'unlettered' | 'partially_lettered' | 'fully_lettered'
  transaction: {
    id: string
    status: string
    residence_id: string
    fiscal_year_id: string
    transaction_date?: string
    description?: string
    piece_number?: string | null
    reference?: string | null
  }
  account: {
    id: string
    code: string
    name: string
  } | null
  owner: {
    id: string
    first_name: string
    last_name: string
  } | null
  unit: {
    id: string
    unit_number: string
  } | null
}

/**
 * Hook pour récupérer les statistiques de lettrage des comptes 450 (copropriétaires)
 * Regroupe les lignes par compte auxiliaire (450 + numéro lot)
 * Utilise la vue transaction_lines_with_lettrage pour les montants lettrés
 */
export function useAccountsLettrageStats(filters: LettrageFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, 'accounts-stats', filters],
    queryFn: async () => {
      // Requête pour obtenir les statistiques par compte/lot/propriétaire
      // Utilise la vue transaction_lines_with_lettrage pour avoir les montants lettrés
      const { data, error } = await supabase
        .from('transaction_lines_with_lettrage')
        .select(`
          id,
          residence_chart_account_id,
          debit,
          credit,
          lettered_amount,
          remaining_amount,
          lettrage_status,
          owner_id,
          unit_id,
          transaction:transactions!inner(
            id,
            status,
            residence_id,
            fiscal_year_id
          ),
          residence_chart_account:residence_chart_accounts(
            id,
            custom_code,
            custom_name,
            chart_account:chart_accounts(id, code, name)
          ),
          owner:owners(
            id,
            first_name,
            last_name
          ),
          unit:units(
            id,
            unit_number
          )
        `)
        .eq('transaction.residence_id', filters.residence_id)
        .eq('transaction.status', 'validated')
        .like('residence_chart_account.chart_account.code', '450%') as { data: TransactionLineWithLettrage[] | null; error: Error | null }

      if (error) throw error

      // Regrouper par unit (un lot = un compte auxiliaire virtuel 450xxx)
      const statsMap = new Map<string, AccountLettrageStats>()
      const unitOwnersMap = new Map<string, Set<string>>()

      // Premier passage : collecter tous les propriétaires uniques par lot
      data?.forEach((line) => {
        const owner = line.owner
        const key = line.unit_id || 'no-unit'

        if (owner && line.unit_id) {
          if (!unitOwnersMap.has(key)) {
            unitOwnersMap.set(key, new Set())
          }
          const ownerName = `${owner.first_name} ${owner.last_name}`
          unitOwnersMap.get(key)!.add(ownerName)
        }
      })

      data?.forEach((line) => {
        const account = (line as any).residence_chart_account?.chart_account
        const owner = line.owner
        const unit = line.unit

        if (!account) return

        // Clé unique : unit_id uniquement (un lot = un seul compte virtuel 450xxx)
        const key = line.unit_id || 'no-unit'

        if (!statsMap.has(key)) {
          // Utiliser custom_code/custom_name de residence_chart_account si disponible
          const residenceAccount = (line as any).residence_chart_account
          const accountCode = residenceAccount?.custom_code || account.code
          const accountName = residenceAccount?.custom_name || account.name

          // Formater les propriétaires : jusqu'à 3 noms, puis +X
          let ownerName: string | null = null
          if (unitOwnersMap.has(key)) {
            const owners = Array.from(unitOwnersMap.get(key)!)
            if (owners.length <= 3) {
              ownerName = owners.join(', ')
            } else {
              const displayedOwners = owners.slice(0, 3).join(', ')
              const remainingCount = owners.length - 3
              ownerName = `${displayedOwners} +${remainingCount}`
            }
          } else if (owner) {
            ownerName = `${owner.first_name} ${owner.last_name}`
          }

          statsMap.set(key, {
            account_id: line.residence_chart_account_id,
            account_code: accountCode,
            account_name: accountName,
            owner_id: owner?.id || null,
            owner_name: ownerName,
            unit_id: unit?.id || null,
            unit_number: unit?.unit_number || null,
            total_debit: 0,
            total_credit: 0,
            balance: 0,
            unlettered_debit: 0,
            unlettered_credit: 0,
            lettered_percentage: 0,
            line_count: 0,
            unlettered_line_count: 0,
          })
        }

        const stats = statsMap.get(key)!
        const debit = line.debit || 0
        const credit = line.credit || 0
        const remaining = line.remaining_amount ?? Math.max(debit, credit)
        const status = line.lettrage_status || 'unlettered'

        stats.total_debit += debit
        stats.total_credit += credit
        stats.line_count += 1

        // Comptabiliser le montant non lettré (remaining_amount)
        if (remaining > 0.01) {
          if (debit > 0) {
            stats.unlettered_debit += remaining
          } else if (credit > 0) {
            stats.unlettered_credit += remaining
          }
        }

        // Compter les lignes non entièrement lettrées
        if (status !== 'fully_lettered') {
          stats.unlettered_line_count += 1
        }
      })

      // Calculer les pourcentages et soldes
      // Convention comptable : Crédit = + (paiement), Débit = - (dette)
      // balance = crédits - débits (négatif = débiteur, positif = créditeur)
      const results: AccountLettrageStats[] = []
      statsMap.forEach((stats) => {
        stats.balance = stats.total_credit - stats.total_debit
        const totalAmount = stats.total_debit + stats.total_credit
        const unletteredAmount = stats.unlettered_debit + stats.unlettered_credit
        const letteredAmount = totalAmount - unletteredAmount
        stats.lettered_percentage = totalAmount > 0 ? Math.round((letteredAmount / totalAmount) * 100) : 100

        results.push(stats)
      })

      // Trier par code compte puis par lot
      return results.sort((a, b) => {
        if (a.account_code !== b.account_code) {
          return a.account_code.localeCompare(b.account_code)
        }
        return (a.unit_number || '').localeCompare(b.unit_number || '')
      })
    },
    enabled: !!filters.residence_id,
  })
}

/**
 * Hook pour récupérer les lignes d'un compte pour le lettrage
 * Sépare les débits (à gauche) des crédits (à droite)
 * Utilise la vue transaction_lines_with_lettrage pour les montants lettrés/restants
 */
export function useAccountLinesForLettrage(
  accountId: string | null,
  unitId: string | null,
  filters: LettrageFilters
) {
  return useQuery({
    queryKey: [QUERY_KEY, 'account-lines', accountId, unitId, filters],
    queryFn: async () => {
      if (!accountId) return { debits: [], credits: [] }

      // Construire la requête avec la vue enrichie
      let query = supabase
        .from('transaction_lines_with_lettrage')
        .select(`
          id,
          transaction_id,
          residence_chart_account_id,
          debit,
          credit,
          label,
          lettered_amount,
          remaining_amount,
          lettrage_status,
          owner_id,
          unit_id,
          transaction:transactions!inner(
            id,
            transaction_date,
            description,
            piece_number,
            reference,
            status,
            residence_id,
            fiscal_year_id
          ),
          residence_chart_account:residence_chart_accounts(
            id,
            custom_code,
            custom_name,
            chart_account:chart_accounts(id, code, name)
          ),
          owner:owners(
            id,
            first_name,
            last_name
          ),
          unit:units(
            id,
            unit_number
          )
        `)
        .eq('residence_chart_account_id', accountId)
        .eq('transaction.residence_id', filters.residence_id)
        .eq('transaction.status', 'validated')

      // Filtrer par lot si spécifié
      if (unitId) {
        query = query.eq('unit_id', unitId)
      }

      // Filtrer par exercice si spécifié
      if (filters.fiscal_year_id) {
        query = query.eq('transaction.fiscal_year_id', filters.fiscal_year_id)
      }

      // Filtrer lettré/non lettré - basé sur remaining_amount > 0
      // Note: on ne peut pas filtrer sur les colonnes calculées directement
      // Le filtrage sera fait côté client

      const { data, error } = (await query) as {
        data: TransactionLineWithLettrage[] | null
        error: Error | null
      }

      if (error) throw error

      // Récupérer les codes de lettrage pour chaque ligne
      const lineIds = (data || []).map(l => l.id)
      let lettrageCodes: Record<string, string[]> = {}

      if (lineIds.length > 0) {
        const { data: lettrageData } = await supabase
          .from('lettrage_lines')
          .select(`
            transaction_line_id,
            lettrage:lettrages(display_code)
          `)
          .in('transaction_line_id', lineIds)

        // Grouper les codes par ligne
        for (const ll of lettrageData || []) {
          const code = (ll as any).lettrage?.display_code
          if (code) {
            if (!lettrageCodes[ll.transaction_line_id]) {
              lettrageCodes[ll.transaction_line_id] = []
            }
            if (!lettrageCodes[ll.transaction_line_id]!.includes(code)) {
              lettrageCodes[ll.transaction_line_id]!.push(code)
            }
          }
        }
      }

      // Transformer et séparer les débits des crédits
      // On garde TOUTES les lignes (allDebits/allCredits) pour le calcul des soldes
      // Et on filtre pour l'affichage (debits/credits)
      const allDebits: LettrageEntryLine[] = []
      const allCredits: LettrageEntryLine[] = []
      const debits: LettrageEntryLine[] = []
      const credits: LettrageEntryLine[] = []

      // Trier par date de transaction côté client
      const sortedData = [...(data || [])].sort((a, b) => {
        const dateA = a.transaction?.transaction_date || ''
        const dateB = b.transaction?.transaction_date || ''
        return dateA.localeCompare(dateB)
      })

      sortedData.forEach((line) => {
        const tx = line.transaction
        const account = (line as any).residence_chart_account?.chart_account
        const owner = line.owner
        const unit = line.unit

        if (!tx || !account) return

        const debit = line.debit || 0
        const credit = line.credit || 0
        const letteredAmount = line.lettered_amount ?? 0
        const remainingAmount = line.remaining_amount ?? Math.max(debit, credit)
        const status = line.lettrage_status || 'unlettered'
        const codes = lettrageCodes[line.id] || []

        const entryLine: LettrageEntryLine = {
          id: line.id,
          transaction_id: line.transaction_id,
          account_id: line.residence_chart_account_id,
          debit,
          credit,
          label: line.label,
          // Nouveau système de lettrage par montant
          lettered_amount: letteredAmount,
          remaining_amount: remainingAmount,
          lettrage_status: status,
          lettrage_codes: codes,
          // Anciens champs pour compatibilité (deprecated)
          lettrage_id: status === 'fully_lettered' ? 'lettered' : null,
          lettrage_code: codes.length > 0 ? codes.join(', ') : null,
          transaction_date: tx.transaction_date || '',
          description: tx.description || '',
          piece_number: tx.piece_number || null,
          reference: tx.reference || null,
          account_code: account.code,
          account_name: account.name,
          owner_id: line.owner_id,
          owner_name: owner ? `${owner.first_name} ${owner.last_name}` : null,
          unit_id: line.unit_id,
          unit_number: unit?.unit_number || null,
        }

        // Ajouter aux listes complètes (pour calcul des soldes)
        if (debit > 0) {
          allDebits.push(entryLine)
        } else if (credit > 0) {
          allCredits.push(entryLine)
        }

        // Ajouter aux listes filtrées (pour affichage)
        const showLine = filters.show_lettered !== false || status !== 'fully_lettered'
        if (showLine) {
          if (debit > 0) {
            debits.push(entryLine)
          } else if (credit > 0) {
            credits.push(entryLine)
          }
        }
      })

      return { debits, credits, allDebits, allCredits }
    },
    enabled: !!accountId && !!filters.residence_id,
  })
}

/**
 * Hook pour créer un lettrage par montant
 * Nouvelle signature acceptant les montants par ligne
 */
export function useCreateLettrage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      accountId,
      lineAmounts,
    }: {
      accountId: string
      lineAmounts: LettrageLineAmount[]
    }) => {
      // Convertir au format attendu par la fonction SQL
      const p_line_amounts = lineAmounts.map(la => ({
        line_id: la.lineId,
        amount: la.amount,
      }))

      const { data, error } = await supabase.rpc('create_lettrage', {
        p_account_id: accountId,
        p_line_amounts,
      })

      if (error) throw error
      return data as string // ID du lettrage créé
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['owner_balances'] })
    },
  })
}

/**
 * Hook pour supprimer un lettrage (délettrer)
 */
export function useDeleteLettrage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (lettrageId: string) => {
      const { error } = await supabase.rpc('delete_lettrage', {
        p_lettrage_id: lettrageId,
      })

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: ['owner_balances'] })
    },
  })
}

/**
 * Hook pour obtenir le détail d'un lettrage avec ses lignes et montants
 */
export function useLettrage(lettrageId: string | null) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', lettrageId],
    queryFn: async () => {
      if (!lettrageId) return null

      const { data, error } = await supabase
        .from('lettrages')
        .select(`
          *,
          lettrage_lines(
            id,
            amount,
            transaction_line:transaction_lines(
              id,
              debit,
              credit,
              label,
              residence_chart_account:residence_chart_accounts(
                id,
                chart_account:chart_accounts(id, code, name)
              ),
              owner:owners(id, first_name, last_name),
              unit:units(id, unit_number),
              transaction:transactions(
                id,
                transaction_date,
                description
              )
            )
          )
        `)
        .eq('id', lettrageId)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!lettrageId,
  })
}
